/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module WafModule
 */

import {
    Component,
    Inject,
    Input,
    OnInit,
} from '@angular/core';
import { StateService } from '@uirouter/core';
import { L10nService } from '@vmw/ngx-vip';
import { EXTERNAL_CLOUD_SERVICES } from 'state-names';

import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import {
    WafApplicationSignatureProvider,
} from 'ajs/modules/waf';

import {
    IWafApplicationSignatureAppVersion,
} from 'generated-types';

import * as globalL10n from 'global-l10n';
import * as l10n from './waf-application-rules-list.l10n';
import './waf-application-rules-list.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description Component for the Waf Application rules List Page.
 * @author Rachit Aggarwal
 */
@Component({
    selector: 'waf-application-rules-list',
    templateUrl: './waf-application-rules-list.component.html',
})
export class WafApplicationRulesListComponent implements OnInit {
    /**
     * WafApplicationSignatureProviderCollection resolved by UI router.
     */
    @Input('resolveApplicationSignatureProvider')
    public applicationSignatureProvider: WafApplicationSignatureProvider;

    /**
     * List of Applications in application rules DB.
     */
    public applications: IWafApplicationSignatureAppVersion[] = [];

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Grid config for the datagrid containing a list of applications.
     */
    public readonly applicationsGridConfig: IAviDataGridConfig;

    constructor(
        l10nService: L10nService,
        @Inject('$state')
        private readonly $state: StateService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.applicationsGridConfig = {
            fields: [
                {
                    label: l10nService.getMessage(l10nKeys.applicationNameLabel),
                    id: 'application-name',
                    transform(app: IWafApplicationSignatureAppVersion) {
                        return app.application;
                    },
                },
                {
                    label: l10nService.getMessage(l10nKeys.lastUpdateVersionLabel),
                    id: 'last-updated-version',
                    transform(app: IWafApplicationSignatureAppVersion) {
                        return app.last_changed_ruleset_version;
                    },
                },
                {
                    label: l10nService.getMessage(l10nKeys.numberOfRulesLabel),
                    id: 'number-of-rules',
                    transform(app: IWafApplicationSignatureAppVersion) {
                        return app.number_of_rules.toString();
                    },
                },
            ],
            getUniqueRowId: (app: IWafApplicationSignatureAppVersion) => app.application,
            layout: {
                hideCheckboxes: true,
            },
        };
    }

    /**
     * Redirects to the Cloud services settings page.
     */
    public redirectToCloudServices(): void {
        this.$state.go(EXTERNAL_CLOUD_SERVICES);
    }

    /** @override */
    public ngOnInit(): void {
        this.applications = this.applicationSignatureProvider.availableApplications || [];
    }
}
